import type { Asset } from '@schibsted-svp/svp-api-types';
import type { Player } from '@schibsted-svp/web-player';

import type { RegisteredPlugin, Plugin } from '../../types/global';
import { getAssetImage, getRawAsset } from '../../utils/player/asset';

const isPlayingState = (state: ReturnType<Player['getState']>): boolean => {
  return ['playing', 'buffering', 'loading'].includes(state);
};

export interface MediaSessionPluginOptions {
  titleOverride?: string;
  hideTitle?: boolean;
}

export interface MediaSessionPlugin extends Plugin<
  MediaSessionPlugin,
  { options?: MediaSessionPluginOptions }
> {
  options: MediaSessionPluginOptions;
  getTitle(
    this: RegisteredPlugin<MediaSessionPlugin>,
    asset: Pick<Asset, 'id' | 'title'>
  ): string;
  updateMetadata(this: RegisteredPlugin<MediaSessionPlugin>): void;
  updatePlaybackState(this: RegisteredPlugin<MediaSessionPlugin>): void;
  seekBackward(this: RegisteredPlugin<MediaSessionPlugin>): void;
  seekForward(this: RegisteredPlugin<MediaSessionPlugin>): void;
  onPlayerTime(
    this: RegisteredPlugin<MediaSessionPlugin>,
    position: number,
    duration: number
  ): void;
}

const userAgentMatch = (matcher: RegExp) => {
  return navigator.userAgent.match(matcher) !== null;
};

const isAndroid = userAgentMatch(/Android/i);
const isFirefox = userAgentMatch(/Firefox\//i);

function keysOf<T extends object>(o: T): (keyof T)[] {
  return Object.keys(o) as (keyof T)[];
}

export const plugin: MediaSessionPlugin = {
  options: {},

  initialize(config) {
    this.options = config?.options ?? {};
  },

  getName() {
    return 'MediaSessionPlugin';
  },

  setup() {
    if ('mediaSession' in navigator) {
      this.listenTo(this.player, 'initialPlay', this.updateMetadata.bind(this));
      this.listenTo(
        this.player,
        'play pause error stop complete',
        this.updatePlaybackState.bind(this)
      );
    }
  },

  updatePlaybackState() {
    const state = this.player.getState();
    if (isPlayingState(state)) {
      navigator.mediaSession.playbackState = 'playing';
    } else {
      navigator.mediaSession.playbackState =
        state === 'paused' ? state : 'none';
    }
  },

  getTitle(asset) {
    const { titleOverride, hideTitle } = this.options;

    if (hideTitle) {
      return '';
    }

    // titleOverride is limited to the asset the player was configured with
    const { id, asset: configAsset } = this.player.getRawConfig();
    if (titleOverride && asset.id === (id ?? configAsset?.id)) {
      return titleOverride;
    }

    return asset.title;
  },

  updateMetadata() {
    const asset = getRawAsset(this.player);

    if (!asset) {
      return;
    }

    const image = getAssetImage(asset, 'main');

    const transforms = {
      '96x96': '120x120q80',
      '128x128': '160x160q80',
      '192x192': '220x220q80',
      '256x256': '320x320q50',
      '384x384': '440x440q50',
      '512x512': '576x576q50',
    };

    const thumbs = keysOf(transforms).map((size) => ({
      src: `${image}?t[]=${transforms[size]}`,
      sizes: size,
      type: 'image/jpeg',
    }));

    const metadata: MediaMetadataInit = {
      title: this.getTitle(asset),
      album: asset.category.title,
      artwork: thumbs,
    };

    // firefox's implementation on android seems to be buggy, the image URLs
    // are being lowercased, which then results in broken URLs to non-existent images
    if (isAndroid && isFirefox) {
      metadata.artwork = undefined;
    }

    navigator.mediaSession.metadata = new MediaMetadata(metadata);

    navigator.mediaSession.setActionHandler(
      'seekbackward',
      this.seekBackward.bind(this)
    );
    navigator.mediaSession.setActionHandler(
      'seekforward',
      this.seekForward.bind(this)
    );

    // @ts-expect-error some browsers do not support this method
    if (navigator.mediaSession.setPositionState) {
      this.listenTo(this.player, 'time', this.onPlayerTime.bind(this));
    }
  },

  seekBackward() {
    const position = this.player.getCurrentTime();

    this.player.seek(Math.max(0, position - 10));
  },

  seekForward() {
    const position = this.player.getCurrentTime();
    const duration = this.player.getDuration();

    this.player.seek(Math.min(duration, position + 10));
  },

  onPlayerTime(position, duration) {
    navigator.mediaSession.setPositionState({
      // Chrome won't accept Infinity here (duration of live asset)
      duration: duration === Infinity ? position : duration,
      playbackRate: 1,
      position,
    });
  },
};

window.SVP.Player.Plugins.register(plugin);
